'use client';

import {
  Divider,
  DividerMods,
  SectionMerits,
} from '@vk-cloud-solutions/marketing-ui';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { AnchorMenu } from '~components/AnchorMenu/AnchorMenu';
import { LayoutContainer } from '~components/LayoutContainer/LayoutContainer';
import { LayoutContainerMods } from '~components/LayoutContainer/LayoutContainer.types';
import { PageUtils } from '~components/Page/Page.utils';
import { env } from '~constants/env';
import { PopupManager } from '~containers/PopupManager/PopupManager';
import { DeployEnvironments } from '~deploy-config/enviroments';
import { useBreakpoints } from '~hooks/useBreakpoints';
import { useCategory } from '~hooks/useCategory';
import { useHeaderVisibility } from '~hooks/useHeaderVisibility';
import { useTheme } from '~hooks/useTheme';
import { McsApi } from '~services/McsApi';

import styles from './Page.module.scss';
import { PageProps } from './Page.types';

const TRIGGER_HEIGHT = 200;

const uiConfig = env.blogConfig?.ui;
const headerShown = uiConfig?.header?.show ?? true;
const footerShown = uiConfig?.footer.show ?? true;
const sectionMeritsShown = uiConfig?.blockMerits?.show ?? true;

const Header = PageUtils.getHeaderComponent(env.deployEnv);
const Footer = PageUtils.getFooterComponent(env.deployEnv);

export const Page = ({
  children,
  footer,
  header,
  menu,
  sectionMerits,
}: PageProps) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [breakpoint] = useBreakpoints();
  const pathname = usePathname();
  const { setCategory } = useCategory();
  const { setHeaderVisible } = useHeaderVisibility();

  const { theme } = useTheme();

  const resetCategory = () => {
    setCategory(undefined);
  };

  useEffect(() => {
    if (env.deployEnv === DeployEnvironments.VkCloudRu) {
      McsApi.getApi();
    }
  }, []);

  useEffect(() => {
    if (pathname === '/blog') {
      resetCategory();
    }
  }, [pathname, setCategory]);

  useEffect(() => {
    const headerCurrent = headerRef.current;
    const menuCurrent = menuRef.current;
    const hero = document.querySelector('[data-id="hero"]');
    const scrollPosition = () =>
      window.scrollY || document.documentElement.scrollTop;
    let lastScroll = 0;
    let isHeaderUp = false;

    document.documentElement.style.setProperty(
      '--calculated-anchor-menu-height',
      `${menuCurrent?.clientHeight || '0'}px`,
    );

    const updateHeader = () => {
      const heroBottom = hero?.getBoundingClientRect().bottom;
      const headerHeight =
        headerCurrent?.getBoundingClientRect().height || TRIGGER_HEIGHT;
      const isSettings =
        (heroBottom && heroBottom <= 0) || scrollY > headerHeight;
      if (isSettings) {
        headerCurrent?.classList.add(styles.sticky);
        isHeaderUp = scrollPosition() <= lastScroll;

        if (isHeaderUp) {
          headerCurrent?.classList.add(styles.up);
          menuCurrent?.classList.add(styles.menuTop);
          setHeaderVisible(true);
        } else {
          headerCurrent?.classList.remove(styles.up);
          menuCurrent?.classList.remove(styles.menuTop);
          setHeaderVisible(false);
        }
      } else {
        headerCurrent?.classList.remove(styles.up, styles.sticky);
        setHeaderVisible(false);
      }

      lastScroll = scrollPosition();
    };

    window.addEventListener('scroll', updateHeader);

    return () => {
      window.removeEventListener('scroll', updateHeader);
    };
  }, [breakpoint, setHeaderVisible]);

  return (
    <div className={classNames(styles.body, styles[`theme--${theme}`])}>
      {headerShown && Header && (
        // @ts-ignore
        <Header {...header} headerRef={headerRef} mod={theme} />
      )}

      {menu && <AnchorMenu {...menu} menuRef={menuRef} mod={theme} />}

      {!menu && (
        <LayoutContainer mods={[LayoutContainerMods.Inner]}>
          <Divider
            className={styles.divider}
            mods={[DividerMods.withoutMargin]}
          />
        </LayoutContainer>
      )}
      <main>{children}</main>
      <PopupManager locale="ru" theme={theme} />
      {sectionMeritsShown && sectionMerits && (
        <SectionMerits
          {...sectionMerits}
          patternSrc={`${env.static}/images/pattern.svg`}
        />
      )}
      {/* @ts-ignore */}
      {footerShown && Footer && <Footer {...footer} />}
    </div>
  );
};
